import type {
  CommercialAcquisitionStatus,
  KanpoNoticeType,
  ListingFilter,
  RegistryTagMatchBasis,
  StoredPictureStatus,
} from "./types";

// 一覧APIの1リクエストあたりの取得上限（BE の上限と同じ）。これを超える件数は絞り込みを促す。
export const CORPORATE_MASTER_LIST_LIMIT = 1000;

// gBiz の法人種別（kind）候補。複数選択で kind__in（完全一致）に渡す。
export const corporateKinds = [
  "株式会社",
  "有限会社",
  "合同会社",
  "合名会社",
  "合資会社",
  "一般社団法人",
  "一般財団法人",
  "特定非営利活動法人",
  "協同組合",
  "相互会社",
];

// 商業登記の取得状況（複数選択でOR）。値は BE の commercial_status と同じ。
export const COMMERCIAL_ACQUISITION_STATUSES = [
  "acquired",
  "in_progress",
  "registration_in_progress",
  "failed",
  "not_acquired",
] as const satisfies readonly CommercialAcquisitionStatus[];

const commercialStatusLabels = {
  acquired: "取得済み",
  in_progress: "取得中",
  registration_in_progress: "登記事件中",
  failed: "失敗",
  not_acquired: "未取得",
} as const satisfies Record<CommercialAcquisitionStatus, string>;

export const commercialStatusOptions = COMMERCIAL_ACQUISITION_STATUSES.map(
  (value) => ({ value, label: commercialStatusLabels[value] })
);

// 上場区分（単一選択。未選択=すべて）
export const LISTING_FILTERS = [
  "listed",
  "unlisted",
] as const satisfies readonly ListingFilter[];

const listingLabels = {
  listed: "上場",
  unlisted: "非上場",
} as const satisfies Record<ListingFilter, string>;

export const listingOptions = LISTING_FILTERS.map((value) => ({
  value,
  label: listingLabels[value],
}));

// 取得済み商業登記のステータス表示。null（未確定）は「取得中」。
export const storedPictureStatusLabels = {
  succeeded: "取得済み",
  failed: "取得失敗",
} as const satisfies Record<StoredPictureStatus, string>;
export const STORED_PICTURE_IN_PROGRESS_LABEL = "取得中";

// 業種の検索条件（日本標準産業分類の大分類）。BE の INDUSTRY_MAJOR_LABELS と同じ並び・名称。
export const INDUSTRY_MAJOR_CATEGORIES = [
  { code: "A", label: "農業，林業" },
  { code: "B", label: "漁業" },
  { code: "C", label: "鉱業，採石業，砂利採取業" },
  { code: "D", label: "建設業" },
  { code: "E", label: "製造業" },
  { code: "F", label: "電気・ガス・熱供給・水道業" },
  { code: "G", label: "情報通信業" },
  { code: "H", label: "運輸業，郵便業" },
  { code: "I", label: "卸売業，小売業" },
  { code: "J", label: "金融業，保険業" },
  { code: "K", label: "不動産業，物品賃貸業" },
  { code: "L", label: "学術研究，専門・技術サービス業" },
  { code: "M", label: "宿泊業，飲食サービス業" },
  { code: "N", label: "生活関連サービス業，娯楽業" },
  { code: "O", label: "教育，学習支援業" },
  { code: "P", label: "医療，福祉" },
  { code: "Q", label: "複合サービス事業" },
  { code: "R", label: "サービス業（他に分類されないもの）" },
  { code: "S", label: "公務（他に分類されるものを除く）" },
  { code: "T", label: "分類不能の産業" },
] as const;

export type IndustryMajorCode =
  (typeof INDUSTRY_MAJOR_CATEGORIES)[number]["code"];

export const INDUSTRY_MAJOR_CODES = INDUSTRY_MAJOR_CATEGORIES.map(
  (category) => category.code
) as [IndustryMajorCode, ...IndustryMajorCode[]];

export const industryLabelByCode: Record<IndustryMajorCode, string> =
  Object.fromEntries(
    INDUSTRY_MAJOR_CATEGORIES.map((category) => [category.code, category.label])
  ) as Record<IndustryMajorCode, string>;

/** 法人名が未登録のときに法人名の代わりに表示する文言 */
export const NO_CORPORATE_NAME_LABEL = "（法人名なし）";

// 官報タブに表示する公告の種別名
export const KANPO_NOTICE_TYPE_LABELS = {
  dissolution: "解散公告",
  settlement: "決算公告",
  merger: "合併公告",
  company_split: "会社分割公告",
  capital_reduction: "減資公告",
  reserve_reduction: "準備金の減少公告",
  organization_change: "組織変更公告",
  bankruptcy: "破産（法人）",
  special_liquidation: "特別清算",
  civil_rehabilitation: "民事再生",
} as const satisfies Record<KanpoNoticeType, string>;

// 公的名簿から決まるタグを、画面では「公表情報」と呼ぶ（名簿を集めた印象を避けるため）
export const PUBLIC_REGISTRY_LABEL = "公表情報";

// 紐付けの方法の名前。マークの読み上げと凡例に使う。画面に出す表示名はここだけが持つ（API は方法のコードだけを返す）
export const MATCH_BASIS_LABELS: Record<RegistryTagMatchBasis, string> = {
  NUMBER: "法人番号で一致",
  ADDRESS: "法人名と住所で一致",
  BRANCH: "同名の法人が全国で1社",
};

// 凡例の並び（確かな順）
export const MATCH_BASIS_ORDER: readonly RegistryTagMatchBasis[] = [
  "NUMBER",
  "ADDRESS",
  "BRANCH",
];

// 一覧の行・カードに出すタグの数。残りは「ほか N」にまとめる
export const LIST_REGISTRY_TAG_LIMIT = 2;
